// 📁 v-permission.ts
import { DirectiveBinding, watch, nextTick, Directive } from "vue";
import { usePermissoesStore } from "@/stores/app/permissoesStore";
import { useAuthStoreUser } from "@/stores/app/authUser";
import { IdReferencia } from "@/shared/perms.config";
import { IdInbox } from "@/services/common-types";

let instanceCounter = 0;

export type InboxPerm = {
  perms: IdReferencia|IdReferencia[],
  idInbox: IdInbox | '',
  invert?: boolean
}

export type vPermissionParams = IdReferencia | IdReferencia[] | InboxPerm;

export type vPermissaoHTMLElement = HTMLElement & {
  __vPermissionWatchers  ?: (() => void)[],
  __vPermissionInstanceId?: string,
  __vPermissionState     ?: { jaVerificou: boolean, temPermissao: boolean },
}

export const vPermission: Directive<HTMLElement, vPermissionParams> = {
  mounted(el: HTMLElement, binding: DirectiveBinding<vPermissionParams>) {
    instanceCounter++;
    const instanceId = `[v-permission #${instanceCounter}]`;
    // const permissaoSolicitada = Array.isArray(binding.value)
    //   ? binding.value.join(", ")
    //   : binding.value;

    // console.log(`\n${instanceId} ========== MOUNTED ==========`);
    // console.log(`${instanceId} Permissão solicitada: ${permissaoSolicitada}`);
    // console.log(`${instanceId} Elemento: ${el.tagName} ${el.className}`);

    const permissoesStore = usePermissoesStore();
    const authStore = useAuthStoreUser();

    const watchers: Array<() => void> = [];

    // ✅ NOVO: Armazena o estado de verificação desta permissão específica
    const ultimaVerificacao: {
      jaVerificou: boolean;
      temPermissao: boolean;
    } = {
      jaVerificou: false,
      temPermissao: false,
    };

    /**
     * ✅ Verifica se as permissões foram carregadas
     */
    const permissoesCarregadas = () => {
      const qtd = Object.keys(permissoesStore.minhasPermissoes).length;
      const result = qtd > 0;
      // console.log(
      //   `${instanceId} [permissoesCarregadas] Dados: ${qtd} contas. Resultado: ${result}`,
      // );

      // ✅ MANTÉM O SIDE EFFECT (como você pediu)
      if (!result) {
        // console.log(
        //   `${instanceId} ⏳ Permissões ainda não carregadas. ESCONDENDO elemento...`,
        // );
        el.style.display = "none";
      }
      return result;
    };

    /**
     * ✅ Atualiza a visibilidade do elemento
     */
    const atualizarVisibilidade = () => {
      // console.log(`\n${instanceId} >>> atualizarVisibilidade() CHAMADO`);
      if (binding.value === undefined) {
        // console.log(`${instanceId} 🔓 Sem restrição (undefined)! MOSTRANDO`);
        el.style.display = "";
        ultimaVerificacao.jaVerificou = true;
        ultimaVerificacao.temPermissao = true;
        return; // ← SAI AQUI, não executa resto do código
      }

      // ✅ Se as permissões ainda não foram carregadas, ESCONDE
      if (!permissoesCarregadas()) {
        // console.log(
        //   `${instanceId} ⏳ Permissões ainda não carregadas. ESCONDENDO elemento...`,
        // );
        // console.log(
        //   `${instanceId}   authStore.initialized: ${authStore.initialized}`,
        // );
        // console.log(
        //   `${instanceId}   currentConta: ${authStore.currentConta?.id || "NENHUMA"}`,
        // );
        el.style.display = "none";

        // ✅ MARCA QUE AINDA NÃO VERIFICOU
        ultimaVerificacao.jaVerificou = false;
        return;
      }

      // console.log(`${instanceId} ✅ Permissões CARREGADAS. Verificando...`);

      const idConta = authStore.currentConta?.id;
      // console.log(`${instanceId} Conta: ${idConta || "NENHUMA"}`);

      if (!idConta) {
        console.warn(`${instanceId} ❌ Sem conta! ESCONDENDO`);
        el.style.display = "none";
        ultimaVerificacao.jaVerificou = true;
        ultimaVerificacao.temPermissao = false;
        return;
      }

      const idInbox = (binding.value as InboxPerm).idInbox
      const paramIsObject = !!idInbox;
      const rawPerms = paramIsObject ? (binding.value as InboxPerm).perms : binding.value;
      const permissoes: IdReferencia[] = Array.isArray(rawPerms)
        ? rawPerms
        : [rawPerms] as IdReferencia[];
      const invertPermCheck = paramIsObject ? !!(binding.value as InboxPerm).invert : false;

      // console.log(`${instanceId} Verificando: [${permissoes.join(", ")}]`);

      // ✅ Verifica se tem QUALQUER UMA das permissões
      let temPermissao = permissoes.some((perm) => {
        // 🔍 Se a permissão começa com "inbox.", e um id de inbox foi fornecido, checa para esse id de inbox específico
        if (perm.startsWith("inbox.")) {

          if (idInbox) {
            const temPermissaoParaEsseInbox = permissoesStore.temPermissao(
              perm,
              idConta,
              idInbox,
            );
            return temPermissaoParaEsseInbox;
          } else { // senão, verifica se a permissão existe em qualquer um dos inboxes conectados
            
            const inboxes =
              permissoesStore.minhasPermissoes[idConta]?.inboxes || {};
            const inboxIds = Object.keys(inboxes);
    
            // console.log(
            //   `${instanceId}   🔍 Permissão de INBOX detectada: ${perm}`,
            // );
            // console.log(
            //   `${instanceId}   📦 Inboxes disponíveis: [${inboxIds.join(", ")}]`,
            // );
  
            // Verifica se TEM a permissão em QUALQUER inbox
            const temEmAlgumaInbox = inboxIds.some((idInbox) => {
              const resultado = permissoesStore.temPermissao(
                perm,
                idConta,
                idInbox,
              );
              // console.log(
              //   `${instanceId}     ${resultado ? "✅" : "❌"} ${perm} na inbox ${idInbox}`,
              // );
              return resultado;
            });
  
            if (perm == 'inbox.apagar_grupos') { console.log('tem permissao para qualquer inbox?', binding, temEmAlgumaInbox); }
            return temEmAlgumaInbox;
          }
        }

        // 🏢 Se NÃO começa com "inbox.", é permissão de CONTA
        const resultado = permissoesStore.temPermissao(perm, idConta, null);
        // console.log(
        //   `${instanceId}   ${resultado ? "✅" : "❌"} ${perm} (nível conta)`,
        // );
        return resultado;
      });

      if (invertPermCheck) temPermissao = !temPermissao;

      // if ((binding.value as InboxPerm).perms == 'inbox.ver_todos_atendimentos') {
        // console.log(
        //   `${instanceId} Resultado: ${temPermissao ? "✅ TEM PERMISSÃO" : "❌ NÃO TEM"}`, binding,
        // );
      // }

      // ✅ ATUALIZA O CACHE
      ultimaVerificacao.jaVerificou = true;
      ultimaVerificacao.temPermissao = temPermissao;

      // ✅ Atualiza a visibilidade BASEADO NA VERIFICAÇÃO
      if (temPermissao) {
        // console.log(`${instanceId} 🟢 display = "" (MOSTRANDO)`);
        el.style.display = "";
      } else {
        // console.log(
        //   `${instanceId} 🔴 display = "none" (ESCONDENDO - sem permissão)`,
        // );
        el.style.display = "none";
      }
    };

    // console.log(`${instanceId} Estado INICIAL:`);
    // console.log(`${instanceId}   initialized: ${authStore.initialized}`);
    // console.log(
    //   `${instanceId}   currentConta: ${authStore.currentConta?.id || "NENHUMA"}`,
    // );
    // console.log(
    //   `${instanceId}   permissões carregadas: ${permissoesCarregadas()}`,
    // );

    // ✅ Watch 1: Quando as permissões mudam
    // console.log(`${instanceId} Registrando Watch 1: minhasPermissoes`);
    const stopPermWatch = watch(
      () => permissoesStore.minhasPermissoes,
      async (newVal, oldVal) => {
        // console.log(`\n${instanceId} 🔔 WATCH 1: minhasPermissoes mudou!`);
        // console.log(
        //   `${instanceId}   Contas antigas: ${Object.keys(oldVal || {}).length}`,
        // );
        // console.log(
        //   `${instanceId}   Contas novas: ${Object.keys(newVal || {}).length}`,
        // );

        // ✅ NOVO: Só atualiza se realmente mudou algo relevante
        const contasAntigas = Object.keys(oldVal || {}).length;
        const contasNovas = Object.keys(newVal || {}).length;

        if (contasNovas > 0) {
          await nextTick();
          atualizarVisibilidade();
        }
      },
      { deep: true },
    );
    watchers.push(stopPermWatch);

    // ✅ Watch 2: Quando a conta muda
    // console.log(`${instanceId} Registrando Watch 2: currentConta`);
    const stopContaWatch = watch(
      () => authStore.currentConta?.id,
      async (newVal, oldVal) => {
        // console.log(`\n${instanceId} 🔔 WATCH 2: currentConta mudou!`);
        // console.log(
        //   `${instanceId}   De: ${oldVal || "NENHUMA"} → Para: ${newVal || "NENHUMA"}`,
        // );

        // ✅ NOVO: Força re-verificação quando conta muda
        if (newVal !== oldVal) {
          ultimaVerificacao.jaVerificou = false;
          await nextTick();
          atualizarVisibilidade();
        }
      },
    );
    watchers.push(stopContaWatch);

    // ✅ Watch 3: Quando authStore inicializa
    // console.log(`${instanceId} Registrando Watch 3: authStore.initialized`);
    const stopAuthInitWatch = watch(
      () => authStore.initialized,
      async (newVal, oldVal) => {
        // console.log(`\n${instanceId} 🔔 WATCH 3: authStore.initialized mudou!`);
        // console.log(`${instanceId}   De: ${oldVal} → Para: ${newVal}`);

        if (newVal && !oldVal) {
          await nextTick();
          atualizarVisibilidade();
        }
      },
    );
    watchers.push(stopAuthInitWatch);

    // console.log(`${instanceId} ✅ 3 Watchers registrados!`);

    // ✅ CHAMA IMEDIATAMENTE para configurar estado inicial
    void atualizarVisibilidade();

    // console.log(`${instanceId} ========== FIM MOUNTED ==========\n`);

    // ✅ Armazenar watchers e estado para cleanup
    const extEl = el as vPermissaoHTMLElement;
    extEl.__vPermissionWatchers   = watchers;
    extEl.__vPermissionInstanceId = instanceId;
    extEl.__vPermissionState      = ultimaVerificacao;  // ✅ NOVO
  },

  unmounted(el: HTMLElement) {
    const extEl = el as vPermissaoHTMLElement;
    const instanceId = extEl.__vPermissionInstanceId || "[v-permission ?]";
    // console.log(`\n${instanceId} ========== UNMOUNTED ==========`);

    const watchers = extEl.__vPermissionWatchers as
      | Array<() => void>
      | undefined;

    if (watchers) {
      // console.log(`${instanceId} Limpando ${watchers.length} watchers...`);
      watchers.forEach((stop, index) => {
        stop();
        // console.log(`${instanceId}   ✅ Watch ${index + 1} removido`);
      });
      // console.log(`${instanceId} ✅ Cleanup completo!`);
    } else {
      console.warn(`${instanceId} ❌ Nenhum watcher para limpeza!`);
    }

    // ✅ Limpa o estado também
    delete extEl.__vPermissionState;

    // console.log(`${instanceId} ========== FIM UNMOUNTED ==========\n`);
  },
};
